import React from 'react';
import {
    Drawer,
    Box,
    Typography,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Divider,
    IconButton,
    Chip, // 🟢 Importar Chip para la leyenda de colores
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Define la estructura de los filtros para claridad
export const initialFilters = {
    selectedSucursal: '',
    selectedEstadoCita: '',
    selectedCategoria: '',
    nombreCliente: '',
    nombrePaciente: '',
};

// 🟢 FUNCIÓN DE COLORES: Usada para la leyenda y la visualización de eventos
const getEventColor = (estado) => {
    switch (estado) {
        case 'PRG': return '#1976d2'; // Azul (Programado)
        case 'APL': return '#388e3c'; // Verde (Aplicado)
        case 'CAN': return '#d32f2f'; // Rojo (Cancelado)
        case 'PEN': return '#f57c00'; // Naranja (Pendiente)
        default: return '#757575';    // Gris (Otro/Desconocido)
    }
};

const FiltrosCalendario = ({
    open,
    onClose,
    filters,
    onFilterChange,
    onSearch,
    onResetSearch,
    sucursales,
    estadosCita,
    categorias,
    loading,
}) => {
    // Manejadores de cambio genéricos para los filtros
    const handleChange = (prop) => (event) => {
        onFilterChange(prop, event.target.value);
    };

    // La función principal de búsqueda
    const handleSearchClick = () => {
        onSearch();
    };

    // La función de limpieza
    const handleResetClick = () => {
        onResetSearch();
        onClose(); // Opcional: Cerrar el drawer al limpiar la búsqueda
    };

    const isAnyFilterActive = filters.selectedSucursal || 
                             filters.selectedEstadoCita || 
                             filters.selectedCategoria || 
                             filters.nombreCliente || 
                             filters.nombrePaciente;

    // 🟢 DATOS PARA LA LEYENDA
    const leyenda = [
        { codigo: 'PRG', nombre: 'Programado', color: getEventColor('PRG') },
        { codigo: 'APL', nombre: 'Aplicado', color: getEventColor('APL') },
        { codigo: 'CAN', nombre: 'Cancelado', color: getEventColor('CAN') },
        { codigo: 'PEN', nombre: 'Pendiente', color: getEventColor('PEN') },
    ];
    // Se puede añadir un color 'default' si quieres representar estados no listados
    // { codigo: 'OTR', nombre: 'Otro/Desconocido', color: getEventColor('OTR') },


    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                // 🟢 Se aumentó ligeramente el ancho para que la leyenda se vea mejor
                sx: { width: 350, p: 3 }, 
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5">
                    Filtros de Citas
                </Typography>
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Filtro: Todas las sucursales */}
                <FormControl fullWidth size="small">
                    <InputLabel id="sucursal-select-label">Todas las sucursales</InputLabel>
                    <Select
                        labelId="sucursal-select-label"
                        value={filters.selectedSucursal}
                        label="Todas las sucursales"
                        onChange={handleChange('selectedSucursal')}
                    >
                        <MenuItem value="">Todas las sucursales</MenuItem>
                        {sucursales.map((sucursal) => (
                            <MenuItem key={sucursal.id_sucursal} value={sucursal.id_sucursal}>
                                {sucursal.nombre_sucursal}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Filtro: Estado Cita */}
                <FormControl fullWidth size="small">
                    <InputLabel id="estado-cita-select-label">Estado Cita</InputLabel>
                    <Select
                        labelId="estado-cita-select-label"
                        value={filters.selectedEstadoCita}
                        label="Estado Cita"
                        onChange={handleChange('selectedEstadoCita')}
                    >
                        <MenuItem value="">Todos los estados</MenuItem>
                        {estadosCita.map((estado) => (
                            <MenuItem key={estado.value} value={estado.value}>
                                {estado.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Filtro: Categoría */}
                <FormControl fullWidth size="small">
                    <InputLabel id="categoria-select-label">Categoría</InputLabel>
                    <Select
                        labelId="categoria-select-label"
                        value={filters.selectedCategoria}
                        label="Categoría"
                        onChange={handleChange('selectedCategoria')}
                    >
                        <MenuItem value="">Todas las categorías</MenuItem>
                        {categorias.map((categoria) => (
                            <MenuItem key={categoria.tab_categoria} value={categoria.tab_categoria}>
                                {categoria.tab_categoria}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Filtro: Nombre del Cliente */}
                <TextField
                    label="Nombre del Cliente"
                    value={filters.nombreCliente}
                    onChange={handleChange('nombreCliente')}
                    size="small"
                />

                {/* Filtro: Nombre del Paciente */}
                <TextField
                    label="Nombre del Paciente"
                    value={filters.nombrePaciente}
                    onChange={handleChange('nombrePaciente')}
                    size="small"
                />

                <Divider sx={{ mt: 1, mb: 1 }} />

                {/* 🟢 SECCIÓN: Leyenda de Colores */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mt: 1 }}>
                        Leyenda de Colores (Estado Cita)
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {leyenda.map((item) => (
                            <Box key={item.codigo} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                    label={item.codigo}
                                    size="small"
                                    sx={{ 
                                        bgcolor: item.color, 
                                        color: 'white', 
                                        fontWeight: 'bold', 
                                        minWidth: '60px', 
                                        justifyContent: 'center',
                                        // Ajustar el color del texto para el gris por defecto
                                        ...(item.codigo === 'OTR' && { color: 'black' }), 
                                    }}
                                />
                                <Typography variant="body2">{item.nombre}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
                
                <Divider sx={{ mt: 1, mb: 1 }} />
                {/* ------------------------------------- */}

                {/* Botón: Buscar */}
                <Button
                    variant="contained"
                    onClick={handleSearchClick}
                    // 🟢 CONDICIÓN MODIFICADA: Deshabilita solo si NO hay ningún filtro activo
                    disabled={loading || !isAnyFilterActive}
                    sx={{ py: 1.5 }}
                >
                    Buscar
                </Button>

                {/* Botón: Limpiar Búsqueda */}
                <Button
                    variant="outlined"
                    onClick={handleResetClick}
                    disabled={loading}
                    sx={{ py: 1.5 }}
                >
                    Limpiar Búsqueda
                </Button>
            </Box>
        </Drawer>
    );
};

export default FiltrosCalendario;